import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cn } from '@/lib/utils';
import { COARSE_HIT_SWITCH } from '@/lib/touch-target';

/*
 * #2423: an OFF switch used to be a `bg-input` track (90% grey light, 19%
 * dark) holding a `bg-background` thumb, about 1.25:1 and 1.3:1 against its
 * card, so an off switch read as empty space. Off is now an OUTLINED track:
 * the boundary and a smaller thumb take `muted-foreground`, which clears 3:1
 * on the card, the page and a popover in both themes. On keeps the filled
 * primary track with a full-size `background` thumb. index.css.test.ts reads
 * these classes back out of this file and measures them.
 *
 * D-2420: the drawn 36x20 track keeps its size; on a coarse pointer an
 * invisible ::after reaches it to a 44 px square (`COARSE_HIT_SWITCH`).
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      COARSE_HIT_SWITCH,
      'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-transparent data-[state=checked]:bg-primary data-[state=unchecked]:border-muted-foreground data-[state=unchecked]:bg-input',
      className
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none block rounded-full shadow-lg ring-0 transition-all data-[state=checked]:h-4 data-[state=checked]:w-4 data-[state=checked]:translate-x-4 data-[state=checked]:bg-background data-[state=unchecked]:h-3 data-[state=unchecked]:w-3 data-[state=unchecked]:translate-x-0.5 data-[state=unchecked]:bg-muted-foreground'
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
